import React, { useState, useEffect } from "react";
import SocialAuthButtons from "../components/SocialAuthButtons";
import { buildLoginUrl } from "@/api/auth/oidcConfig";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { goToLandingSite } from "@/lib/siteUrls";
import { Eye, EyeOff, Moon, Sun, User, Mail, Lock, Loader2, CheckCircle, AlertCircle, Globe } from "lucide-react";
import { register } from "@/api/auth";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { COUNTRIES } from "@/constants/countries";


const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, toggleTheme, setUser } = useApp();
  const { t, i18n } = useTranslation();

  const isDark = theme === "dark";

  // Keep the display label ("EN" / "FR") in sync with the global i18n instance
  const [language, setLanguage] = useState<"EN" | "FR">(
    i18n.language?.startsWith("fr") ? "FR" : "EN"
  );

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedRole, setSelectedRole] = useState<"operator" | "end-user">("end-user");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);

  // React to any language change (from Header toggle or this page's own toggle)
  useEffect(() => {
    const handleLangChange = (lng: string) => {
      setLanguage(lng.startsWith("fr") ? "FR" : "EN");
    };
    i18n.on("languageChanged", handleLangChange);
    return () => {
      i18n.off("languageChanged", handleLangChange);
    };
  }, [i18n]);

  // Pre-fill referral code from URL query param (?ref=XXXX)
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData((prev) => ({ ...prev, referralCode: refCode }));
    }
  }, [searchParams]);

  useEffect(() => {
    const token = searchParams.get('token');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      const errorMessages: Record<string, string> = {
        'google_auth_failed': t('register.errorMessages.google_auth_failed', { defaultValue: 'Google authentication failed. Please try again.' }),
        'token_generation_failed': t('register.errorMessages.token_generation_failed', { defaultValue: 'Authentication successful but token generation failed. Please try again.' }),
        'auth_failed': t('register.errorMessages.auth_failed', { defaultValue: 'Authentication failed. Please try again.' })
      };

      setError(errorMessages[errorParam] || t('register.errorMessages.auth_failed', { defaultValue: 'Authentication failed. Please try again.' }));
      setOauthLoading(false);

      window.history.replaceState({}, '', '/register');
      return;
    }

    // The BFF signals a completed OAuth round-trip via ?success=true (the session
    // is already set as an HttpOnly cookie). We no longer accept a token in the URL.
    const success = searchParams.get('success');
    if (success === 'true' || token) {
      handleOAuthCallback();
    }
  }, [searchParams]);


  const handleOAuthCallback = async () => {
    try {
      setLoading(true);
      setOauthLoading(true);

      // Identity ADR: the session is established by the BFF via an HttpOnly cookie
      // — we do NOT read a token from the URL or store one in localStorage. Fetch
      // the profile using the cookie (credentials included).
      const response = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const payload = await response.json();
      const userData = payload?.data?.user ?? payload;

      const user = {
        id: userData._id || userData.id,
        name: userData.firstName && userData.lastName
          ? `${userData.firstName} ${userData.lastName}`
          : userData.firstName || userData.email.split('@')[0],
        email: userData.email,
        role: userData.role as "admin" | "organization" | "operator" | "end-user",
        firstName: userData.firstName,
        lastName: userData.lastName,
        emailVerified: userData.isVerified || true,
        avatar: userData.profile?.avatar || userData.avatar,
        initials: userData.firstName && userData.lastName
          ? `${userData.firstName.charAt(0)}${userData.lastName.charAt(0)}`.toUpperCase()
          : userData.firstName
          ? userData.firstName.substring(0, 2).toUpperCase()
          : userData.email.substring(0, 2).toUpperCase()
      };

      console.log("✅ OAuth registration successful:", user);

      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setSuccess(true);

      window.history.replaceState({}, '', '/register');

      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1500);

    } catch (err: unknown) {
      console.error("❌ OAuth callback error:", err);
      setError((err instanceof Error ? err.message : "An error occurred") || t('register.errorMessages.auth_failed', { defaultValue: "Authentication failed. Please try again." }));
      localStorage.removeItem('user');
      setOauthLoading(false);
    } finally {
      setLoading(false);
    }
  };
const handleGoogleRegister = () => {
  setOauthLoading(true);
  setError("");
  const rolePath = selectedRole === "operator" ? "operator" : "end-user";
  window.location.href = `${API_URL}/auth/google/${rolePath}`;
};

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (formData.password !== formData.confirmPassword) {
      setError(t("register.validation.passwordsMismatch", { defaultValue: "Passwords do not match" }));
      return;
    }

    if (formData.password.length < 8) {
      setError(t("register.validation.passwordTooShort", { defaultValue: "Password must be at least 8 characters long" }));
      return;
    }

    if (!agreedToTerms) {
      setError(t("register.validation.termsRequired", { defaultValue: "Please agree to the terms and conditions" }));
      return;
    }

    if (!formData.country) {
      setError(t("register.validation.countryRequired", { defaultValue: "Please select your country" }));
      return;
    }


    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionId');

    setLoading(true);

    try {
      const requestData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        country: formData.country,
        password: formData.password,
        role: selectedRole,
        referralCode: formData.referralCode || undefined,
      };

      console.log("📝 Submitting registration:", requestData);
      const response = await register(requestData);
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response from server');
      }

      console.log("📧 Full registration response:", response);
      console.log("📧 needsVerification:", response.needsVerification);
      console.log("📧 token:", response.token ? "EXISTS" : "NOT PRESENT");

      // ✅ CASE 1: User needs email verification (operator/end-user)
      if (response.needsVerification === true) {
        console.log("✅ Needs verification - redirecting to /verify-email");
        console.log("✅ Email to pass:", formData.email);

        setLoading(false);
        setSuccess(true);

        setTimeout(() => {
          navigate("/verify-email", {
            replace: true,
            state: {
              email: formData.email
            }
          });
        }, 1500);
        return;
      }

      
      if (response.token && !response.needsVerification) {
        console.log("✅ Admin user - has token, no verification needed");
        setLoading(false);
        setSuccess(true);

        setTimeout(() => {
          window.location.href = response.redirectPath || "/dashboard";
        }, 1500);
        return;
      }

      // ❌ CASE 3: Unexpected response
      console.error("⚠️ Unexpected registration response:", response);
      setError(t("register.validation.verificationUnknown", { defaultValue: "Registration completed but verification status unclear." }));
      setLoading(false);

    } catch (err: unknown) {
      console.error("❌ Registration error:", err);

      const errorMessages: Record<string, string> = {
        'timeout of 60000ms exceeded': t('register.errors.timeout', { defaultValue: 'Request is taking too long. Please try again.' }),
        'timeout': t('register.errors.timeout', { defaultValue: 'Request is taking too long. Please try again.' }),
        'ECONNABORTED': t('register.errors.connection', { defaultValue: 'Connection interrupted. Please check your internet connection.' }),
        'Network Error': t('register.errors.network', { defaultValue: 'Unable to connect. Please check your internet connection.' }),
        'Network request failed': t('register.errors.network', { defaultValue: 'Unable to connect. Please check your internet connection.' }),
        'Failed to fetch': t('register.errors.network', { defaultValue: 'Unable to connect. Please check your internet connection.' }),
        'Email already exists': t('register.errors.emailExists', { defaultValue: 'This email is already registered. Try signing in instead.' }),
        'User already exists': t('register.errors.userExists', { defaultValue: 'An account with this email already exists.' }),
        'Invalid email format': t('register.errors.invalidEmail', { defaultValue: 'Please enter a valid email address.' }),
        'Weak password': t('register.errors.weakPassword', { defaultValue: 'Password must contain uppercase, lowercase, number and special character.' }),
        'Invalid referral code': t('register.errors.invalidReferral', { defaultValue: 'The referral code you entered is invalid.' }),
      };

      let errorMessage = (err instanceof Error ? err.message : "An error occurred") || t('register.errors.generic', { defaultValue: "Registration failed. Please try again." });

      for (const [key, value] of Object.entries(errorMessages)) {
        if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
          errorMessage = value;
          break;
        }
      }

      if (typeof err === 'object' && err !== null && 'code' in err) {
        const code = String((err as { code: unknown }).code);
        if (errorMessages[code]) {
          errorMessage = errorMessages[code];
        }
      }

      setError(errorMessage);
      setLoading(false);
    }
  };


  const inputText = isDark ? "#ffffff" : "#1f2937";
  const labelCol  = isDark ? "#9ca3af" : "#4b5563";
  const iconCol   = isDark ? "#6b7280" : "#9ca3af";
  const selectBorder = isDark ? "#3a3a3a" : "#d1d5db";
  const selectText   = isDark ? "#d1d5db" : "#374151";
  const dividerCol   = isDark ? "#2a2a2a" : "#e5e7eb";
  const dividerText  = isDark ? "#6b7280" : "#6b7280";

  return (
    <>
      <Helmet>
        <title>{t("register.tabregister", { defaultValue: "Register" })} | KonectX</title>
        <meta
          name="description"
          content={t("register.metaDescription", { defaultValue: "Create a new KonectX account to join communities, access hotspots, and interact with your network." })}
        />
        <meta
          name="keywords"
          content="Konectx register, sign up, create account"
        />
        <meta property="og:title" content={`${t("register.tabregister", { defaultValue: "Register" })} - KonectX`} />
        <meta
          property="og:description"
          content={t("register.metaDescription", { defaultValue: "Create a new KonectX account to join communities, access hotspots, and interact with your network." })}
        />
      </Helmet>


      <div
        className="min-h-screen flex flex-col items-center justify-center p-4 pt-24 sm:pt-4 transition-colors duration-300 bg-gradient-to-br from-blue-500 via-purple-500 to-purple-600"
      >
 
        {success && (
          <div className="fixed top-6 right-6 z-50">
            <div className="bg-green-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-right">
              <CheckCircle className="w-6 h-6" />
              <div>
                <p className="font-semibold">{t("register.successmessage1", { defaultValue: "Registration Successful!" })}</p>
                <p className="text-sm text-green-100">{t("register.successmessage2", { defaultValue: "Redirecting..." })}</p>
              </div>
            </div>
          </div>
        )}

   
        {oauthLoading && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div
              className="px-8 py-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4"
              style={{ backgroundColor: isDark ? "#000000" : "#ffffff" }}
            >
              <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
              <p className="font-medium" style={{ color: isDark ? "#e5e7eb" : "#374151" }}>
                {t("register.authenticating", { defaultValue: "Authenticating..." })}
              </p>
            </div>
          </div>
        )}


        {/* Language Toggle */}
        <button
          onClick={() => {
            const next = language === "EN" ? "fr" : "en";
            i18n.changeLanguage(next);
            localStorage.setItem("i18nextLng", next);
          }}
          className="absolute top-6 right-20 inline-flex items-center gap-2 px-3 py-2 rounded-lg transition select-none"
          style={{
            backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            color: isDark ? "#ffffff" : "#374151",
            border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
          }}
          aria-label={t("register.languageToggle", { defaultValue: `Switch language to ${language === "EN" ? "French" : "English"}` })}
        >
          <Globe size={18} style={{ strokeWidth: 1.8 }} />
          <span className="hidden sm:inline text-sm font-semibold tracking-wide">{language}</span>
          <span className="sm:hidden text-xs font-bold tracking-tight">{language}</span>
        </button>

        <button
          onClick={toggleTheme}
          className="absolute top-6 right-6 p-2 rounded-lg transition"
          style={{
            backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            color: isDark ? "#ffffff" : "#374151",
          }}
        >
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        {/* ── Back Home — redirect to the landing site (env-configured) ──── */}
        <button
          onClick={() => goToLandingSite(navigate)}
          className="absolute top-6 left-4 sm:left-6 transition text-sm sm:text-base flex items-center gap-1 z-20"
          style={{ color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)" }}
        >
          <span className="hidden sm:inline">← {t("register.backbutton", { defaultValue: "Back Home" })}</span>
          <span className="sm:hidden text-xs font-semibold">← {t("login.homemobile", { defaultValue: "Home" })}</span>
        </button>

        <div className="w-full max-w-md">
          <div className="flex gap-4 justify-center mb-8">
            {/* Active */}
            <button
              className="flex-1 sm:flex-none px-6 sm:px-12 py-3 rounded-full text-base sm:text-lg font-medium shadow-lg transition"
              style={{
                backgroundColor: isDark ? "#333436"  : "#ffffff",
                color: isDark ? "#ffffff" : "#1f2937",
              }}
            >
              {t("register.tabregister", { defaultValue: "Register" })}
            </button>
            {/* Inactive */}
            <button
              onClick={() => navigate("/signin")}
              className="flex-1 sm:flex-none px-6 sm:px-12 py-3 rounded-full text-base sm:text-lg font-medium transition"
              style={{
                backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                color: isDark ? "rgba(255,255,255,0.75)" : "#374151",
              }}
            >
              {t("login.tablogin", { defaultValue: "Login" })}
            </button>
          </div>

        
          <div
            className="rounded-3xl shadow-2xl p-6 sm:p-10 transition-colors duration-300"
            style={{ backgroundColor: isDark ? "#333436" : "#ffffff" }}
          >
            {/* Header row */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: isDark ? "#ffffff" : "#1f2937" }}>
                {t("register.headersignup", { defaultValue: "Register" })}
              </h2>

              {/* Role Selector */}
              <div className="relative w-full sm:w-auto">
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as "operator" | "end-user")}
                  className="w-full sm:min-w-[160px] px-4 py-2.5 sm:py-2 rounded-xl cursor-pointer appearance-none pr-10 outline-none transition text-sm shadow-sm"
                  style={{
                    backgroundColor: isDark ? "#2a2c2e" : "#f9fafb",
                    border: isDark ? "1px solid #4a4c4e" : "1px solid #d1d5db",
                    color: isDark ? "#d1d5db" : "#374151",
                  }}
                  disabled={loading || success || oauthLoading}
                >
                  <option value="end-user">{t("register.dropdownoptionuser", { defaultValue: "Register as User" })}</option>
                  <option value="operator">{t("register.dropdownoptionoperator", { defaultValue: "Register as Operator" })}</option>
                </select>
                <svg
                  className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: isDark ? "#9ca3af" : "#6b7280" }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* ── Role Badge ──────────────────────────────────────────── */}
            <div className="mb-6">
              <span
                className="inline-block px-4 py-1.5 rounded-full text-sm font-medium"
                style={
                  selectedRole === "operator"
                    ? {
                        backgroundColor: isDark ? "rgba(59,130,246,0.15)" : "#dbeafe",
                        color: isDark ? "#60a5fa" : "#1d4ed8",
                      }
                    : {
                        backgroundColor: isDark ? "rgba(34,197,94,0.15)" : "#dcfce7",
                        color: isDark ? "#4ade80" : "#15803d",
                      }
                }
              >
                {selectedRole === "operator"
                  ? t("register.roleBadge.operator", { defaultValue: "Operator Account" })
                  : t("register.roleBadge.user", { defaultValue: "User Account" })}
              </span>
            </div>

            {/* ── Error Banner ────────────────────────────────────────── */}
            {error && (
              <div
                className="mb-4 p-4 rounded-lg"
                style={{
                  backgroundColor: isDark ? "rgba(220,38,38,0.12)" : "#fef2f2",
                  border: isDark ? "1px solid rgba(220,38,38,0.35)" : "1px solid #fecaca",
                }}
              >
                <div className="flex items-start gap-3">
                  <AlertCircle
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: isDark ? "#f87171" : "#dc2626" }}
                  />
                  <div className="flex-1">
                    <p className="text-sm" style={{ color: isDark ? "#f87171" : "#dc2626" }}>
                      {error}
                    </p>
                    {error.toLowerCase().includes("server") && (
                      <button
                        onClick={() => setError("")}
                        className="mt-2 text-xs underline hover:no-underline"
                        style={{ color: isDark ? "#f87171" : "#b91c1c" }}
                      >
                        {t("register.errors.dismiss", { defaultValue: "Dismiss and try again" })}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Form ────────────────────────────────────────────────── */}
            <form onSubmit={handleRegister} className="space-y-4">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: labelCol }}>
                  {t("register.firstname", { defaultValue: "First Name" })}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: iconCol }}>
                    <User size={20} />
                  </div>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder={t("register.firstnameplaceholder", { defaultValue: "Enter your first name" })}
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl outline-none transition placeholder-gray-400"
                    style={{  backgroundColor: isDark ? "#2a2c2e" : "#f3f4f6", color: inputText }}
                    onFocus={(e) => (e.target.style.boxShadow = "inset 0 0 0 2px #7c3aed")}
                    onBlur={(e) => (e.target.style.boxShadow = "none")}
                    required
                    disabled={loading || success || oauthLoading}
                    minLength={2}
                    maxLength={30}
                  />
                </div>
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: labelCol }}>
                  {t("register.lastname", { defaultValue: "Last Name" })}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: iconCol }}>
                    <User size={20} />
                  </div>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder={t("register.lastnameplaceholder", { defaultValue: "Enter your last name" })}
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl outline-none transition placeholder-gray-400"
                    style={{  backgroundColor: isDark ? "#2a2c2e" : "#f3f4f6", color: inputText }}
                    onFocus={(e) => (e.target.style.boxShadow = "inset 0 0 0 2px #7c3aed")}
                    onBlur={(e) => (e.target.style.boxShadow = "none")}
                    required
                    disabled={loading || success || oauthLoading}
                    minLength={2}
                    maxLength={30}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: labelCol }}>
                  {t("register.email", { defaultValue: "Email Address" })}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: iconCol }}>
                    <Mail size={20} />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("register.emailplaceholder", { defaultValue: "Enter your email" })}
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl outline-none transition placeholder-gray-400"
                    style={{  backgroundColor: isDark ? "#2a2c2e" : "#f3f4f6", color: inputText }}
                    onFocus={(e) => (e.target.style.boxShadow = "inset 0 0 0 2px #7c3aed")}
                    onBlur={(e) => (e.target.style.boxShadow = "none")}
                    required
                    disabled={loading || success || oauthLoading}
                  />
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: labelCol }}>
                  {t("register.country", { defaultValue: "Country" })}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: iconCol }}>
                    <Globe size={20} />
                  </div>
                  <input
                    type="text"
                    name="country"
                    list="country-options"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder={t("register.countryPlaceholder", { defaultValue: "Search or select your country" })}
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl outline-none transition placeholder-gray-400"
                    style={{ backgroundColor: isDark ? "#2a2c2e" : "#f3f4f6", color: inputText }}
                    onFocus={(e) => (e.target.style.boxShadow = "inset 0 0 0 2px #7c3aed")}
                    onBlur={(e) => (e.target.style.boxShadow = "none")}
                    required
                    disabled={loading || success || oauthLoading}
                    autoComplete="off"
                  />
                  <datalist id="country-options">
                    {COUNTRIES.map((country) => (
                      <option key={country} value={country} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: labelCol }}>
                  {t("register.password", { defaultValue: "Password" })}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: iconCol }}>
                    <Lock size={20} />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t("register.passwordplaceholder", { defaultValue: "Create your Password (min 8 chars)" })}
                    className="w-full pl-12 pr-12 py-3.5 rounded-xl outline-none transition placeholder-gray-400"
                    style={{  backgroundColor: isDark ? "#2a2c2e" : "#f3f4f6", color: inputText }}
                    onFocus={(e) => (e.target.style.boxShadow = "inset 0 0 0 2px #7c3aed")}
                    onBlur={(e) => (e.target.style.boxShadow = "none")}
                    required
                    disabled={loading || success || oauthLoading}
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 transition"
                    style={{ color: iconCol }}
                    disabled={loading || success || oauthLoading}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: labelCol }}>
                  {t("register.confirmpassword", { defaultValue: "Confirm Password" })}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: iconCol }}>
                    <Lock size={20} />
                  </div>
                  <input
                    type={showConfirm ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder={t("register.confirmpasswordplaceholder", { defaultValue: "Confirm your Password" })}
                    className="w-full pl-12 pr-12 py-3.5 rounded-xl outline-none transition placeholder-gray-400"
                    style={{  backgroundColor: isDark ? "#2a2c2e" : "#f3f4f6", color: inputText }}
                    onFocus={(e) => (e.target.style.boxShadow = "inset 0 0 0 2px #7c3aed")}
                    onBlur={(e) => (e.target.style.boxShadow = "none")}
                    required
                    disabled={loading || success || oauthLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 transition"
                    style={{ color: iconCol }}
                    disabled={loading || success || oauthLoading}
                  >
                    {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Referral Code */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: labelCol }}>
                  {t("register.referral", { defaultValue: "Referral Code (optional)" })}
                </label>
                <input
                  type="text"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                  placeholder={t("register.referralplaceholder", { defaultValue: "Enter Referral Code if you have one" })}
                  className="w-full px-4 py-3.5 rounded-xl outline-none transition placeholder-gray-400"
                  style={{  backgroundColor: isDark ? "#2a2c2e" : "#f3f4f6", color: inputText }}
                  onFocus={(e) => (e.target.style.boxShadow = "inset 0 0 0 2px #7c3aed")}
                  onBlur={(e) => (e.target.style.boxShadow = "none")}
                  disabled={loading || success || oauthLoading}
                />
              </div>

              {/* Terms checkbox */}
              <label className="flex items-start gap-3 cursor-pointer pt-2">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="w-4 h-4 rounded accent-blue-600 mt-0.5"
                  disabled={loading || success || oauthLoading}
                />
                <span className="text-sm" style={{ color: labelCol }}>
                  {t("register.agreetoterm", { defaultValue: "I agree to the Terms of Service and Privacy Policy" })}
                </span>
              </label>

             
              <Button
                type="submit"
                disabled={loading || success || oauthLoading}
                className="w-full text-white font-semibold py-3.5 rounded-xl transition h-auto text-base mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: isDark ? "#2563eb" : "#000000",
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t("register.creatingAccount", { defaultValue: "Creating Account..." })}
                  </span>
                ) : success ? (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {t("register.accountCreated", { defaultValue: "Account Created!" })}
                  </span>
                ) : (
                  t("register.buttonsignup", { defaultValue: "Create Account" })
                )}
              </Button>
            </form>

      
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px" style={{ backgroundColor: dividerCol }}></div>
              <span className="text-sm" style={{ color: dividerText }}>
                {t("register.continuewithheader", { defaultValue: "Or continue with" })}
              </span>
              <div className="flex-1 h-px" style={{ backgroundColor: dividerCol }}></div>
            </div>

        
            <SocialAuthButtons
              selectedRole={selectedRole}
              oauthLoading={oauthLoading}
              setOauthLoading={setOauthLoading}
              disabled={loading || success}
              isDark={isDark}
            />

      
            <div className="mt-6 text-center text-sm" style={{ color: labelCol }}>
              {t("register.haveaccount", { defaultValue: "Already have an account?" })}{" "}
              <button
                onClick={() => navigate("/signin")}
                className="font-semibold transition"
                style={{ color: "#2563eb" }}
                disabled={loading || success || oauthLoading}
              >
                {t("register.haveaccountlink", { defaultValue: "Sign in" })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
